import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Typography from "@mui/material/Typography";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from "@mui/material";

import { cleanUserSession, resetUserSessionDummy, setUserSession, setUserUpdateDummySession, } from "../state/actions";
import { emailDetectedSelector, userDummySelector, userExistSelector } from "../state/selectors/sessionSelectors";
import { LayoutContainer, LayoutWrapper, LogoComponent, } from "../components";
import { TermsLink } from "../components/TermsLink";
import { ButtonMGPrimary } from "../components/buttons";
import { validation } from "../utils";
import LoadingContext from "../context/LoadingProvider";
import SessionService from "../services/SessionService";

const Password = () => {
  const [values, setValues] = useState({ fullname: "", vat: "", password: "", typePass: false });

  const [validFullname, setValidFullname] = useState(true);
  const [validVat, setValidVat] = useState(true);
  const [validPassword, setValidPassword] = useState(true);
  const [shouldRegister, setShouldRegister] = useState(false);

  const { setLoadingValue } = useContext(LoadingContext)
  const userDummy = useSelector(userDummySelector)
  const emailDetected = useSelector(emailDetectedSelector)
  const userExist = useSelector(userExistSelector)
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const welcomeMsg = useMemo(() => !userExist ? "Tu primera gran experiencia" : "Gracias por volver a visitarnos", [userExist])

  const handleChange = (prop: string) => (event: any) => setValues({ ...values, [prop]: event.target.value });
  const handleClickShowPassword = () => setValues({ ...values, typePass: !values.typePass });

  const signIn = () => {
    if (!validation("email", emailDetected) || !values.password.length) { return; }

    try {
      setLoadingValue(true)
      SessionService.loginEmailPassword(emailDetected.toLowerCase(), values.password)
        .then((data) => {
          if (data) {
            dispatch(setUserSession(data));
            navigate("/payment/list");
          } else {
            toast("Error de ingreso - Contraseña incorrecta")
          }
        })
        .finally(() => setLoadingValue(false))
    } catch (error) {
      toast("Error de ingreso - Contraseña incorrecta");
    }
  };

  const signUp = () => {

    const isEmailValid = validation("email", emailDetected);
    const isPasswordValid = values.password.length > 0;
    const isFullnameValid = values.fullname.length > 4;
    const isVatValid = values.vat.length === 8;
  
    setValidPassword(isPasswordValid);
    setValidFullname(isFullnameValid);
    setValidVat(isVatValid);
  
    if (!isFullnameValid) toast.error("El nombre completo es obligatorio");
    if (!isVatValid) toast.error("El documento debe tener 8 dígitos");
    if (!isPasswordValid) toast.error("La contraseña no puede estar vacía");   
  
    const isFormValid = isEmailValid && isPasswordValid && isFullnameValid && isVatValid;
    if (!isFormValid) return;

    try {
      dispatch(
        setUserUpdateDummySession({
          ...userDummy,
          login: emailDetected || userDummy.login,
          password: values.password,
          terms_conditions_agreement: true,
        })
      );
      setLoadingValue(true);
      setShouldRegister(true);
    } catch (error) { setLoadingValue(false) }
  };

  useEffect(() => {
    if (shouldRegister && userDummy?.login && userDummy?.password) {
      registerUser(); // se ejecuta solo una vez
      setShouldRegister(false); // resetea la bandera
    }
  }, [shouldRegister, userDummy]);

  const registerUser = async () => {
    console.log(values.fullname, values.vat);
    SessionService.registrarUsuario({
      name: values.fullname,
      vat: values.vat,
      ...userDummy,
    })
      .then((res) => {
        if (!res?.result) {
          toast("Error al crear el usuario. Intente más tarde");
        } else if (
          res?.result &&
          res?.result?.status &&
          res?.result?.status > 201
        ) {
          toast(res?.result?.message);
          console.log("Falle");
        } else {
          dispatch(
            setUserSession({
              name: values.fullname,
              vat: values.vat,
              ...userDummy,
            })
          );
          navigate("/payment/list");
        }
      })
      .finally(() => setLoadingValue(false));
  };

  const handleCleanUserSession = () => {
    dispatch(cleanUserSession())
    dispatch(resetUserSessionDummy())
  }

  const navigateRecover = () => navigate('/recover-password');

  useEffect(() => {
    if (!(emailDetected || userDummy?.login)) {
      navigate("/");
    }
  }, [emailDetected, userDummy?.login]);

  return (
    <LayoutContainer>
      <LayoutWrapper>
        <LogoComponent />
        <Typography component="h1" variant="h5">
          {welcomeMsg}
        </Typography>

        <Typography component="h1" variant="body2">
          Email: {emailDetected}
        </Typography>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          { !userExist && (
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel htmlFor="outlined-adornment-fullname">Ingrese su nombre completo</InputLabel>
              <OutlinedInput
                autoFocus
                label="Ingrese su nombre completo"
                id="outlined-adornment-fullname"
                value={values.fullname}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const input = e.target.value;
                  const filtered = input.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, "");
                  handleChange("fullname")({
                    ...e,
                    target: {
                      ...e.target,
                      value: filtered,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                inputProps={{
                  inputMode: "text",
                  maxLength: 40
                }}
                type="text"
                error={!validFullname}
              />
            </FormControl>
          )}
          { !userExist && (
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel htmlFor="outlined-adornment-vat">Ingrese su DNI</InputLabel>
              <OutlinedInput
                label="Ingrese su DNI"
                id="outlined-adornment-vat"
                value={values.vat}
                onChange={handleChange("vat")}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                type="number"
                error={!validVat}
              />
            </FormControl>
          )}
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="outlined-adornment-password">
            Ingrese una contraseña
            </InputLabel>
            <OutlinedInput
              label="Ingrese una contraseña"
              id="outlined-adornment-password"
              type={values.typePass ? "text" : "password"}
              value={values.password}
              onChange={handleChange("password")}
              error={!validPassword}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end">
                    {values.typePass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>} />
          </FormControl>

          { userExist && (
            <div
              onClick={navigateRecover}
              style={{ marginTop: 10, color: '#3DDAD0', cursor: 'pointer' }}>
              <Typography component="p" variant="body1">Quiero recuperar mi contraseña.</Typography>
            </div>
          )}

          <ButtonMGPrimary
            title={userExist ? "Ingresar" : "Registrarme"}
            action={() => userExist ? signIn() : signUp()} />

          <ButtonMGPrimary
            title="Usar otro email"
            action={handleCleanUserSession} />

          <TermsLink />
        </Box>
      </LayoutWrapper>
    </LayoutContainer>
  );
};

export default Password;
